import * as React from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./dialog";
import { Button } from "./button";
import { Text, MonoText } from "./typography";
import { CurrencyValue } from "./value-display";
import { Badge } from "./badge";
import { API_BASE_URL } from '../../lib/api';

/**
 * DeleteExpenseDialog - Confirmation dialog for deleting an expense
 */
export function DeleteExpenseDialog({
  open,
  onOpenChange,
  onSuccess,
  expense,
}) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Reset error when dialog opens
  React.useEffect(() => {
    if (open) {
      setError(null);
    }
  }, [open]);

  const handleDelete = async () => {
    if (!expense?.expenseId) {
      setError("No expense selected");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.delete(
        `${API_BASE_URL}/expenses/${expense.expenseId}`,
        { withCredentials: true }
      );

      if (onSuccess) {
        onSuccess();
      }
      onOpenChange(false);
    } catch (err) {
      let errorMsg =
        err.response?.data?.details ||
        err.response?.data?.error ||
        err.message;

      if (typeof errorMsg === "object" && errorMsg !== null) {
        errorMsg = errorMsg.message || JSON.stringify(errorMsg);
      }

      console.error("Failed to delete expense:", err);
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (!expense) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            Delete Expense
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete this
            expense record.
          </DialogDescription>
        </DialogHeader>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive p-3">
            <Text variant="error" size="sm">
              {error}
            </Text>
          </div>
        )}

        {/* Expense Details */}
        <div className="rounded-lg border border-border/50 p-4 space-y-2">
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Name</Text>
            <Text size="sm" className="font-medium">{expense.name}</Text>
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Category</Text>
            <Badge variant="outline" size="sm">{expense.category}</Badge>
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Amount</Text>
            <CurrencyValue
              value={expense.amount}
              currency={expense.currency}
              size="sm"
              className="font-medium"
            />
          </div>
          <div className="flex justify-between items-center">
            <Text variant="secondary" size="sm">Frequency</Text>
            <Text size="sm" className="capitalize">{expense.frequency}</Text>
          </div>
          {expense.renewalDate && (
            <div className="flex justify-between items-center">
              <Text variant="secondary" size="sm">Renewal Date</Text>
              <MonoText size="sm">{expense.renewalDate}</MonoText>
            </div>
          )}
        </div>

        <DialogFooter className="pt-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleDelete}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DeleteExpenseDialog;
